
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useHiddenRoutines } from "./useHiddenRoutines";
import { fetchWithRetry } from "./utils/fetchWithRetry";
import {
  handleAddRoutineToPersonalPlan,
  handleAddRoutineToSharedPlan,
} from "./utils/addRoutineUtils";
import {
  handleRemoveFromPersonalPlan,
  handleRemoveFromSharedPlan,
} from "./utils/removeRoutineUtils";

const formatExercises = (exercises: any[] = []) =>
  exercises
    .map((ex: any) => ({
      ...ex,
      exercise_rest_time:
        ex.exercise_rest_time !== null && ex.exercise_rest_time !== undefined
          ? Number(ex.exercise_rest_time)
          : null,
      set_rest_time:
        ex.set_rest_time !== null && ex.set_rest_time !== undefined
          ? Number(ex.set_rest_time)
          : null,
      set_data: ex.set_data || [],
    }))
    .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0));

/**
 * Inserta la rutina en TODAS las caches que pueden estar pintando el plan.
 * - ["workout-plan-optimized", planId] → la que usa PlanView (con initialPlan)
 * - ["optimized-plan-routines", planId] → la usada cuando NO hay initialPlan
 */
const appendRoutineToAllCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  planId: string,
  routine: any
) => {
  queryClient.setQueryData<any[]>(
    ["optimized-plan-routines", planId],
    (old) => [...(old || []), routine]
  );

  queryClient.setQueryData<any>(
    ["workout-plan-optimized", planId],
    (old) => {
      if (!old) return old;
      return { ...old, routines: [...(old.routines || []), routine] };
    }
  );
};

const removeRoutineFromAllCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  planId: string,
  matchId: string
) => {
  queryClient.setQueryData<any[]>(
    ["optimized-plan-routines", planId],
    (old) => (old || []).filter((r) => r.id !== matchId)
  );
  queryClient.setQueryData<any>(
    ["workout-plan-optimized", planId],
    (old) => {
      if (!old) return old;
      return {
        ...old,
        routines: (old.routines || []).filter((r: any) => r.id !== matchId),
      };
    }
  );
};

const getCurrentRoutinesCount = (
  queryClient: ReturnType<typeof useQueryClient>,
  planId: string
): number => {
  const fromOptimized = queryClient.getQueryData<any>(
    ["workout-plan-optimized", planId]
  );
  if (fromOptimized?.routines) return fromOptimized.routines.length;
  const fromPlanRoutines = queryClient.getQueryData<any[]>([
    "optimized-plan-routines",
    planId,
  ]);
  return fromPlanRoutines?.length ?? 0;
};

export const useRoutineActions = (
  planId: string | undefined,
  onRefetch: () => void
) => {
  const queryClient = useQueryClient();
  const { hideRoutine } = useHiddenRoutines(planId);

  const handleAddRoutine = async (routineId: string, preloadedRoutine?: any) => {
    if (!planId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "ID del plan no encontrado",
      });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuario no autenticado");

      const { data: workoutPlan } = await supabase
        .from("workout_plans")
        .select("is_personal, created_by")
        .eq("id", planId)
        .single();

      const isMyPersonalPlan =
        workoutPlan?.is_personal && workoutPlan?.created_by === user.id;

      // Duplicate check (solo shared plans; el personal siempre crea copia)
      if (!isMyPersonalPlan) {
        const { data: existingRoutine } = await fetchWithRetry(async () => {
          return supabase
            .from("workout_plan_routines")
            .select("id")
            .eq("workout_plan_id", planId)
            .eq("routine_id", routineId)
            .maybeSingle();
        });

        if (existingRoutine) {
          toast({
            variant: "destructive",
            title: "Error",
            description: "Esta rutina ya está en el plan",
          });
          return;
        }
      }

      // Resolver el id final y los exercises a usar
      let finalRoutineId: string;
      let exercises: any[];
      let day: string | null;
      let routineName: string;
      let imageUrl: string | null;

      if (isMyPersonalPlan) {
        // Crear copia
        finalRoutineId = await handleAddRoutineToPersonalPlan(
          routineId,
          planId,
          user.id
        );

        const [{ data: copiedExercises }, { data: copiedDays }, { data: copiedRoutine }] =
          await Promise.all([
            supabase
              .from("exercises")
              .select(
                "id, name, sets, reps, weight, position, set_data, exercise_rest_time, set_rest_time, video_url"
              )
              .eq("routine_id", finalRoutineId)
              .order("position", { ascending: true }),
            supabase
              .from("routine_days")
              .select("day")
              .eq("routine_id", finalRoutineId)
              .limit(1),
            supabase
              .from("routines")
              .select("name, image_url")
              .eq("id", finalRoutineId)
              .single(),
          ]);

        exercises = copiedExercises || [];
        day = copiedDays?.[0]?.day ?? null;
        routineName = copiedRoutine?.name ?? preloadedRoutine?.name ?? "Rutina";
        imageUrl = copiedRoutine?.image_url ?? null;
      } else {
        // Shared plan: vincular rutina existente
        await handleAddRoutineToSharedPlan(routineId, planId);
        finalRoutineId = routineId;

        // Exercises: usar precargados si vinieron del modal, si no traerlos
        if (preloadedRoutine?.exercises?.length) {
          exercises = preloadedRoutine.exercises;
        } else {
          const { data: fetchedExercises, error: exErr } = await supabase
            .from("exercises")
            .select(
              "id, name, sets, reps, weight, position, set_data, exercise_rest_time, set_rest_time, video_url"
            )
            .eq("routine_id", routineId)
            .order("position", { ascending: true });
          if (exErr) throw exErr;
          exercises = fetchedExercises || [];
        }

        // Day
        const preloadedDay =
          preloadedRoutine?.routine_days?.[0]?.day ?? preloadedRoutine?.day;
        if (preloadedDay !== undefined && preloadedDay !== null) {
          day = preloadedDay;
        } else {
          const { data: dayData } = await supabase
            .from("routine_days")
            .select("day")
            .eq("routine_id", routineId)
            .limit(1)
            .maybeSingle();
          day = dayData?.day ?? null;
        }

        routineName = preloadedRoutine?.name ?? "Rutina";
        imageUrl = preloadedRoutine?.image_url ?? null;
      }

      const newRoutine = {
        id: finalRoutineId,
        name: routineName,
        image_url: imageUrl,
        day,
        plan_position: getCurrentRoutinesCount(queryClient, planId),
        exercises: formatExercises(exercises),
      };

      appendRoutineToAllCaches(queryClient, planId, newRoutine);
      queryClient.invalidateQueries({ queryKey: ["filtered-routines", planId] });

      toast({
        title: "Rutina agregada",
        description: "La rutina se ha agregado al plan correctamente",
        variant: "success",
      });
    } catch (error) {
      console.error("Error adding routine:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo agregar la rutina al plan",
      });
      // Red de seguridad: si algo quedó inconsistente, refrescar
      try {
        onRefetch();
      } catch {}
    }
  };

  const handleRemoveRoutine = async (routineId: string) => {
    if (!planId) return;

    // Snapshots para rollback
    const prevOptimized = queryClient.getQueryData<any>([
      "workout-plan-optimized",
      planId,
    ]);
    const prevPlanRoutines = queryClient.getQueryData<any[]>([
      "optimized-plan-routines",
      planId,
    ]);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuario no autenticado");

      const { data: workoutPlan } = await supabase
        .from("workout_plans")
        .select("is_personal, created_by")
        .eq("id", planId)
        .single();

      const isMyPersonalPlan =
        workoutPlan?.is_personal && workoutPlan?.created_by === user.id;

      // Optimistic remove
      removeRoutineFromAllCaches(queryClient, planId, routineId);

      if (isMyPersonalPlan) {
        hideRoutine(routineId);
        await handleRemoveFromPersonalPlan(routineId, planId, user.id);
      } else {
        await handleRemoveFromSharedPlan(routineId, planId);
      }

      // Diferimos la invalidación para no encadenar otro render en el mismo
      // tick que el optimistic remove. Evita inconsistencias de hooks en
      // componentes hijos que se desmontan.
      queueMicrotask(() => {
        queryClient.invalidateQueries({ queryKey: ["filtered-routines", planId] });
      });
    } catch (error) {
      console.error("Error removing routine:", error);
      // Rollback
      if (prevOptimized !== undefined) {
        queryClient.setQueryData(["workout-plan-optimized", planId], prevOptimized);
      }
      if (prevPlanRoutines !== undefined) {
        queryClient.setQueryData(
          ["optimized-plan-routines", planId],
          prevPlanRoutines
        );
      }
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo eliminar la rutina",
      });
    }
  };

  return {
    handleAddRoutine,
    handleRemoveRoutine,
  };
};
